import React from 'react';
import {alpha, Box, Paper, Stack, Typography} from '@mui/material';
import {AutoAwesome, ChatBubbleOutline, Science, PersonOutline} from '@mui/icons-material';

export default function ContextAwareSupportVisual() {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 260,
                p: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{fontWeight: 800}}>
                            Context-aware assistant
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Active formulation support
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            px: 1.2,
                            py: 0.5,
                            borderRadius: 10,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{fontWeight: 800, color: 'primary.main'}}
                        >
                            Context linked
                        </Typography>
                    </Box>
                </Stack>

                {/* Small context strip */}
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        px: 2,
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                        flexWrap: 'wrap',
                        rowGap: 0.75,
                    }}
                >
                    <Science sx={{fontSize: 16, color: 'primary.main'}}/>
                    <Typography variant="caption" color="text.secondary">
                        Greek Style Yogurt · Blueberry prototype v3
                    </Typography>
                </Stack>

                {/* Chat */}
                <Box sx={{p: 2}}>
                    <Stack spacing={1.5}>
                        {/* User message */}
                        <Stack direction="row" spacing={1.2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.4,
                                }}
                            >
                                <PersonOutline sx={{fontSize: 16}}/>
                            </Box>

                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1.2,
                                    borderRadius: 2,
                                    maxWidth: '85%',
                                    boxShadow: 'none',
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <ChatBubbleOutline sx={{fontSize: 16, color: 'primary.main', mt: 0.2}}/>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{display: 'block', mb: 0.4}}>
                                            User
                                        </Typography>
                                        <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.5, fontSize: 14}}>
                                            Is the protein level suitable for a source of protein claim in this
                                            formulation?
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>

                        {/* Assistant message */}
                        <Stack direction="row" spacing={1.2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.4,
                                }}
                            >
                                <AutoAwesome sx={{fontSize: 16}}/>
                            </Box>

                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1.3,
                                    borderRadius: 2,
                                    maxWidth: '88%',
                                    boxShadow: 'none',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                                }}
                            >
                                <Typography variant="caption" color="primary.main"
                                            sx={{display: 'block', mb: 0.5, fontWeight: 800}}>
                                    Assistant
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                    display: 'block', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5,
                                }}
                                >
                                    For the active <strong>Greek Style Yogurt</strong> formulation, protein is
                                    calculated at <strong>5.4 g per 100 g</strong>. This comes mainly from the
                                    dairy ingredients: <strong>milk</strong> at <strong>82.6%</strong> and{' '}
                                    <strong>whole milk powder</strong> at <strong>7.0%</strong>.
                                    <br/>
                                    <br/>
                                    Based on the current nutrition values, the <strong>source of protein</strong>{' '}
                                    claim is marked as eligible.
                                </Typography>
                            </Paper>
                        </Stack>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}